import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import {

    Checkbox,
    FormControlLabel

} from "@mui/material";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";

import {
    CheckCircle,
    CloudUpload,
    Close,
    Delete,
    Error as ErrorIcon,
    InsertDriveFile,
    Refresh
} from "@mui/icons-material";

import AxiosInstance from "../../services/AxiosInstance";

import { useAlert } from "../alerts/AlertContext";
import {IMPORT_URL, IMPORT_TABLES_URL} from "../../Constants/Constants";

const GenericCsvImportForm = ({
    open,
    onClose,
    onImportSuccess
}) => {

    /*
     * ----------------------------------------------------
     * State
     * ----------------------------------------------------
     */

    const [tables, setTables] = useState([]);

    const [selectedTable, setSelectedTable] =
        useState("");

    const [metadata, setMetadata] =
        useState(null);

    const [file, setFile] =
        useState(null);

    const [records, setRecords] =
        useState([]);

    const [errors, setErrors] =
        useState([]);

    const [loadingTables, setLoadingTables] =
        useState(false);

    const [loadingMetadata, setLoadingMetadata] =
        useState(false);

    const [loadingImport, setLoadingImport] =
        useState(false);

    const fileInputRef = useRef(null);


    const [tableMetadata, setTableMetadata] = useState(null);

    const [csvHeaders, setCsvHeaders] = useState([]);

    //const [csvRows, setCsvRows] = useState([]);

    const [validationErrors, setValidationErrors] = useState([]);

    const [validationWarnings, setValidationWarnings] = useState([]);

    const [isCsvValid, setIsCsvValid] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState([]);

    const showAlert = useAlert();
    /*
     * ----------------------------------------------------
     * Load available import tables
     * ----------------------------------------------------
     */

    useEffect(() => {

        if (!open) {
            return;
        }

        loadTables();

    }, [open]);


    const loadTables = async () => {

        try {

            setLoadingTables(true);

            const response =
                await AxiosInstance.get(
                    IMPORT_TABLES_URL
                );

            setTables(
                response.data || []
            );

        } catch (error) {

            console.error(
                "Unable to load tables",
                error
            );

            setErrors([
                "Unable to load import tables."
            ]);

        } finally {

            setLoadingTables(false);
        }
    };


    /*
     * ----------------------------------------------------
     * Table selection
     * ----------------------------------------------------
     */

    const handleTableChange = async (
        event
    ) => {

        const tableCode =
            event.target.value;

        setSelectedTable(tableCode);

        /*
         * Clear previous CSV
         */

        setFile(null);
        setRecords([]);
        setErrors([]);
        setMetadata(null);

        if (fileInputRef.current) {

            fileInputRef.current.value = "";
        }

        if (!tableCode) {
            return;
        }

        await loadMetadata(tableCode);
    };


    /*
     * ----------------------------------------------------
     * Load table metadata
     * ----------------------------------------------------
     */

    const loadMetadata = async (
        tableCode
    ) => {

        try {

            setLoadingMetadata(true);

            const response =
                await AxiosInstance.get(
                    `${IMPORT_URL}/metadata/${tableCode}`
                );

            const metadata = response.data;

            setMetadata(
                response.data
            );

            const columns =
                metadata.columns || [];

            // const selectableColumns =
            //     columns.filter(column =>
            //         !column.identityColumn &&
            //         !column.autoGenerated
            //     );

            // setSelectedColumns(
            //     selectableColumns.map(
            //         column => column.columnName
            //     )
            // );

        } catch (error) {

            console.error(
                "Unable to load metadata",
                error
            );

            setErrors([
                "Unable to load table metadata."
            ]);

        } finally {

            setLoadingMetadata(false);
        }
    };


    /*
     * ----------------------------------------------------
     * Importable columns
     * ----------------------------------------------------
     */

    // const importableColumns = useMemo(() => {

    //     if (!metadata?.columns) {
    //         return [];
    //     }

    //     return metadata.columns.filter(
    //         column =>
    //             column.importable !== false
    //     );

    // }, [metadata]);

    const importableColumns = useMemo(() => {

        if (!metadata?.columns) {
            return [];
        }

        return metadata.columns.filter(
            column =>
                column.importable !== false &&
                !column.identityColumn &&
                !column.autoGenerated
        );

    }, [metadata]);
    /*
     * ----------------------------------------------------
     * Expected headers
     * ----------------------------------------------------
     */

    const expectedHeaders = useMemo(() => {

        return importableColumns.map(
            column =>
                column.columnName
                    .trim()
                    .toLowerCase()
        );

    }, [importableColumns]);


    /*
     * ----------------------------------------------------
     * File selection
     * ----------------------------------------------------
     */

    const handleFileChange = (event) => {

        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        if (
            !file.name
                .toLowerCase()
                .endsWith(".csv")
        ) {
            setValidationErrors([
                "Please select a CSV file."
            ]);
            return;
        }

        setFile(file);

        const reader = new FileReader();

        reader.onload = (e) => {

            const csvText = e.target.result;

            const result = parseCSV(csvText);

            setCsvHeaders(result.headers);

            setRecords(result.rows);

            validateCSVHeaders(result.headers);
        };

        reader.onerror = () => {

            setValidationErrors([
                "Unable to read CSV file."
            ]);
        };

        reader.readAsText(file);
    };


    /*
     * ----------------------------------------------------
     * Parse CSV
     * ----------------------------------------------------
     */

    const parseCSV = (text) => {

        text = text.replace(/^\uFEFF/, "");

        const lines = text
            .split(/\r?\n/)
            .filter(line => line.trim() !== "");

        if (lines.length === 0) {
            return {
                headers: [],
                rows: []
            };
        }

        const headers = parseCSVLine(lines[0])
            .map(header =>
                header.trim().toLowerCase()
            );

        const rows = lines
            .slice(1)
            .map(line => {

                const values = parseCSVLine(line);

                const row = {};

                headers.forEach((header, index) => {

                    row[header] =
                        values[index] !== undefined
                            ? values[index].trim()
                            : "";
                });

                return row;
            });

        return {
            headers,
            rows
        };
    };

    const parseCSVLine = (line) => {

        const result = [];

        let value = "";

        let insideQuotes = false;

        for (let i = 0; i < line.length; i++) {

            const char = line[i];

            if (char === '"') {

                if (
                    insideQuotes &&
                    line[i + 1] === '"'
                ) {

                    value += '"';

                    i++;

                } else {

                    insideQuotes =
                        !insideQuotes;
                }

            } else if (
                char === "," &&
                !insideQuotes
            ) {

                result.push(value);

                value = "";

            } else {

                value += char;
            }
        }

        result.push(value);

        return result;
    };

    const validateCSVHeaders = (uploadedHeaders) => {

        if (!tableMetadata) {
            setValidationErrors([
                "Please select a table first."
            ]);
            setIsCsvValid(false);
            return false;
        }

        // Get columns returned by backend
        const databaseColumns =
            tableMetadata.columns || [];

        // Columns that should not be uploaded
        const systemColumns = [
            "id",
            "created_by",
            "created_on",
            "modified_by",
            "modified_on"
        ];

        // Columns expected in CSV
        const expectedColumns =
            databaseColumns.filter(
                column =>
                    !systemColumns.includes(
                        column.columnName
                            .trim()
                            .toLowerCase()
                    )
            );

        const expectedHeaders =
            expectedColumns.map(
                column =>
                    column.columnName
                        .trim()
                        .toLowerCase()
            );

        // Headers from CSV
        const actualHeaders =
            uploadedHeaders.map(
                header =>
                    header
                        .trim()
                        .toLowerCase()
            );

        const errors = [];

        /*
         * 1. Check empty headers
         */
        if (actualHeaders.length === 0) {

            errors.push(
                "CSV file does not contain headers."
            );
        }

        /*
         * 2. Check duplicate headers
         */
        const duplicates =
            actualHeaders.filter(
                (header, index) =>
                    actualHeaders.indexOf(header) !== index
            );

        const uniqueDuplicates =
            [...new Set(duplicates)];

        if (uniqueDuplicates.length > 0) {

            errors.push(
                `Duplicate columns: ${uniqueDuplicates.join(", ")}`
            );
        }

        /*
         * 3. Check missing columns
         */
        const missingColumns =
            expectedHeaders.filter(
                header =>
                    !actualHeaders.includes(header)
            );

        if (missingColumns.length > 0) {

            errors.push(
                `Missing columns: ${missingColumns.join(", ")}`
            );
        }

        /*
         * 4. Check unexpected columns
         */
        const unexpectedColumns =
            actualHeaders.filter(
                header =>
                    !expectedHeaders.includes(header)
            );

        const uniqueUnexpected =
            [...new Set(unexpectedColumns)];

        if (uniqueUnexpected.length > 0) {

            errors.push(
                `Unexpected columns: ${uniqueUnexpected.join(", ")}`
            );
        }

        /*
         * 5. Update UI
         */
        setValidationErrors(errors);

        const valid = errors.length === 0;

        setIsCsvValid(valid);

        return valid;
    };

    /*
     * ----------------------------------------------------
     * Header validation
     * ----------------------------------------------------
     */

    const validateHeaders = (
        csvHeaders
    ) => {

        const normalizedHeaders =
            csvHeaders.map(
                header =>
                    header
                        .trim()
                        .toLowerCase()
            );

        /*
         * Missing expected headers
         */

        const missingHeaders =
            expectedHeaders.filter(
                expected =>
                    !normalizedHeaders.includes(
                        expected
                    )
            );

        /*
         * Unexpected headers
         */

        const unexpectedHeaders =
            normalizedHeaders.filter(
                actual =>
                    !expectedHeaders.includes(
                        actual
                    )
            );

        /*
         * Duplicate headers
         */

        const duplicateHeaders =
            normalizedHeaders.filter(
                (header, index) =>
                    normalizedHeaders.indexOf(
                        header
                    ) !== index
            );

        if (
            duplicateHeaders.length > 0
        ) {

            unexpectedHeaders.push(
                `Duplicate headers: ${[
                    ...new Set(
                        duplicateHeaders
                    )
                ].join(", ")
                }`
            );
        }

        return {
            missingHeaders,
            unexpectedHeaders
        };
    };


    /*
     * ----------------------------------------------------
     * Row validation
     * ----------------------------------------------------
     */

    const validateRows = (
        rows
    ) => {

        return rows.map(
            (
                row,
                index
            ) => {

                const rowErrors = [];

                const rowNumber =
                    index + 2;

                importableColumns.forEach(
                    column => {

                        const value =
                            row[column.columnName];

                        /*
                         * Required validation
                         */

                        if (
                            column.required === true &&
                            (
                                value === undefined ||
                                value === null ||
                                String(value).trim() === ""
                            )
                        ) {

                            rowErrors.push(
                                `${column.columnName} is required`
                            );

                            return;
                        }

                        /*
                         * Empty optional field
                         */

                        if (
                            value === undefined ||
                            value === null ||
                            String(value).trim() === ""
                        ) {

                            return;
                        }

                        /*
                         * Data type validation
                         */

                        const validType =
                            validateDataType(
                                value,
                                column.dataType
                            );

                        if (!validType) {

                            rowErrors.push(
                                `${column.columnName} has invalid ${column.dataType} value`
                            );
                        }

                        /*
                         * Maximum length
                         */

                        if (
                            column.maxLength &&
                            String(value).length >
                            Number(
                                column.maxLength
                            )
                        ) {

                            rowErrors.push(
                                `${column.columnName} exceeds maximum length ${column.maxLength}`
                            );
                        }
                    }
                );

                return {
                    ...row,
                    _rowNumber: rowNumber,
                    _errors: rowErrors
                };
            }
        );
    };


    /*
     * ----------------------------------------------------
     * Data type validation
     * ----------------------------------------------------
     */

    const validateDataType = (
        value,
        dataType
    ) => {

        const type =
            String(
                dataType || ""
            ).toLowerCase();

        const text =
            String(value).trim();

        /*
         * Integer
         */

        if (
            type === "integer" ||
            type === "int" ||
            type === "bigint" ||
            type === "smallint"
        ) {

            return /^-?\d+$/.test(text);
        }

        /*
         * Decimal / Numeric
         */

        if (
            type === "numeric" ||
            type === "decimal" ||
            type === "real" ||
            type === "double precision"
        ) {

            return (
                text !== "" &&
                !Number.isNaN(
                    Number(text)
                )
            );
        }

        /*
         * Boolean
         */

        if (
            type === "boolean" ||
            type === "bool"
        ) {

            return [
                "true",
                "false",
                "1",
                "0",
                "yes",
                "no"
            ].includes(
                text.toLowerCase()
            );
        }

        /*
         * Date
         */

        if (
            type === "date"
        ) {

            return !Number.isNaN(
                Date.parse(text)
            );
        }

        /*
         * Timestamp
         */

        if (
            type.includes("timestamp")
        ) {

            return !Number.isNaN(
                Date.parse(text)
            );
        }

        /*
         * Time
         */

        if (
            type === "time" ||
            type.includes("time without") ||
            type.includes("time with")
        ) {

            return /^\d{2}:\d{2}(:\d{2})?$/.test(
                text
            );
        }

        /*
         * UUID
         */

        if (
            type === "uuid"
        ) {

            return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
                .test(text);
        }

        /*
         * VARCHAR / TEXT / CHAR
         */

        return true;
    };


    /*
     * ----------------------------------------------------
     * Statistics
     * ----------------------------------------------------
     */

    const totalRecords =
        records.length;


    const validRecords = useMemo(
        () =>
            records.filter(
                row =>
                    !row?._errors ||
                    row._errors.length === 0
            ),
        [records]
    );

    const invalidRecords =
        useMemo(
            () =>
                records.filter(
                    row =>
                        row?._errors?.length > 0
                ),
            [records]
        );


    /*
     * ----------------------------------------------------
     * Remove file
     * ----------------------------------------------------
     */

    const handleRemoveFile = () => {

        setFile(null);
        setRecords([]);
        setErrors([]);

        if (fileInputRef.current) {

            fileInputRef.current.value =
                "";
        }
    };


    /*
     * ----------------------------------------------------
     * Import
     * ----------------------------------------------------
     */

    const handleImport = async () => {

        if (!selectedTable) {

            setErrors([
                "Please select a table."
            ]);

            return;
        }

        if (!file) {

            setErrors([
                "Please select a CSV file."
            ]);

            return;
        }

        if (
            validRecords.length === 0
        ) {

            setErrors([
                "There are no valid records to import."
            ]);

            return;
        }

        try {

            setLoadingImport(true);

            setErrors([]);

            /*
             * Convert CSV data
             * into API payload
             */

            const payloadRecords =
                validRecords.map(
                    row => {

                        const record = {};

                        // importableColumns.forEach(
                        //     column => {

                        //         record[column.columnName] =
                        //             row[column.columnName];
                        //     }
                        // );

                        selectedColumns.forEach(columnName => {
                            record[columnName] =
                                row[columnName];
                        });

                        return record;
                    }
                );

            const request = {

                table:
                    selectedTable,
                columns: selectedColumns,
                records:
                    payloadRecords
            };

            const response =
                await AxiosInstance.post(
                    IMPORT_URL,
                    request
                );


            showAlert(
                response.data?.message ||
                `${response.data?.successRecords || payloadRecords.length} records imported successfully.`,
                "success"
            );

            /*
             * Notify parent
             */

            if (
                onImportSuccess
            ) {

                onImportSuccess(
                    response.data
                );
            }

            /*
             * Close dialog
             */

            resetDialog();

            onClose();

        } catch (error) {

            console.error(
                "Import failed",
                error
            );

            const message =
                error.response?.data?.message ||
                "Unable to import records.";

            setErrors([
                message
            ]);

        } finally {

            setLoadingImport(false);
        }
    };


    /*
     * ----------------------------------------------------
     * Reset dialog
     * ----------------------------------------------------
     */

    const resetDialog = () => {

        setSelectedTable("");
        setMetadata(null);
        setFile(null);
        setRecords([]);
        setErrors([]);

        if (fileInputRef.current) {

            fileInputRef.current.value =
                "";
        }
    };


    /*
     * ----------------------------------------------------
     * Close
     * ----------------------------------------------------
     */

    const handleClose = () => {

        if (loadingImport) {
            return;
        }

        resetDialog();

        onClose();
    };


    /*
     * ----------------------------------------------------
     * Retry metadata
     * ----------------------------------------------------
     */

    const handleRetryMetadata = () => {

        if (selectedTable) {

            loadMetadata(
                selectedTable
            );
        }
    };

    const handleColumnSelection = (
        columnName,
        checked
    ) => {

        setSelectedColumns(prev => {

            if (checked) {

                return [
                    ...prev,
                    columnName
                ];
            }

            return prev.filter(
                column =>
                    column !== columnName
            );
        });
    };

    /*
     * ----------------------------------------------------
     * Render
     * ----------------------------------------------------
     */

    return (

        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="xl"
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    minHeight: "70vh"
                }
            }}
        >

            {/* ==================================================
                TITLE
            ================================================== */}

            <DialogTitle
                sx={{
                    px: 3,
                    py: 2
                }}
            >

                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                >

                    <Box>

                        <Typography
                            variant="h6"
                            fontWeight={600}
                        >
                            Import Data
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Select a table and upload a
                            CSV file for validation
                        </Typography>

                    </Box>

                    <IconButton
                        onClick={handleClose}
                        disabled={loadingImport}
                    >
                        <Close />
                    </IconButton>

                </Box>

            </DialogTitle>

            <Divider />


            {/* ==================================================
                CONTENT
            ================================================== */}

            <DialogContent
                sx={{
                    px: 3,
                    py: 3
                }}
            >

                <Stack spacing={3}>


                    {/* ==========================================
                        TABLE SELECTION
                    ========================================== */}

                    <Box>

                        <Typography
                            variant="subtitle2"
                            fontWeight={600}
                            mb={1}
                        >
                            Select Table
                        </Typography>

                        <FormControl
                            fullWidth
                            size="small"
                        >

                            <InputLabel>
                                Table
                            </InputLabel>

                            <Select
                                value={
                                    selectedTable
                                }
                                label="Table"
                                onChange={
                                    handleTableChange
                                }
                                disabled={
                                    loadingTables ||
                                    loadingMetadata ||
                                    loadingImport
                                }
                            >

                                <MenuItem value="">
                                    Select table
                                </MenuItem>

                                {tables.map(
                                    table => (

                                        <MenuItem
                                            key={
                                                table.tableCode
                                            }
                                            value={
                                                table.tableName
                                            }
                                        >
                                            {
                                                table.displayName
                                            }
                                        </MenuItem>

                                    )
                                )}

                            </Select>

                        </FormControl>

                    </Box>


                    {/* ==========================================
                        METADATA LOADING
                    ========================================== */}

                    {loadingMetadata && (

                        <Box>

                            <LinearProgress />

                            <Typography
                                variant="body2"
                                color="text.secondary"
                                mt={1}
                            >
                                Loading table columns...
                            </Typography>

                        </Box>
                    )}


                    {/* ==========================================
                        EXPECTED HEADERS
                    ========================================== */}

                    {metadata &&
                        !loadingMetadata && (

                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2
                                }}
                            >

                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    mb={1}
                                >

                                    <Box>

                                        <Typography
                                            variant="subtitle2"
                                            fontWeight={600}
                                        >
                                            Expected CSV Headers
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            The CSV must contain
                                            these headers
                                        </Typography>

                                    </Box>

                                    <Chip
                                        size="small"
                                        label={`${importableColumns.length} columns`}
                                    />

                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 1,
                                        mb: 1
                                    }}
                                >
                                    <Button
                                        size="small"
                                        onClick={() => {

                                            const columns =
                                                importableColumns
                                                    ?.filter(
                                                        column =>
                                                            !column.identityColumn &&
                                                            !column.autoGenerated
                                                    )
                                                    .map(
                                                        column =>
                                                            column.columnName
                                                    ) || [];

                                            setSelectedColumns(columns);
                                        }}
                                    >
                                        Select All
                                    </Button>

                                    <Button
                                        size="small"
                                        onClick={() =>
                                            setSelectedColumns([])
                                        }
                                    >
                                        Clear All
                                    </Button>
                                </Box>

                                <Box
                                    display="flex"
                                    gap={1}
                                    flexWrap="wrap"
                                >

                                    {importableColumns?.filter(
                                        column =>
                                            !column.identityColumn &&
                                            !column.autoGenerated
                                    ).map(
                                        column => (

                                            // <Chip
                                            //     key={
                                            //         column.columnName
                                            //     }
                                            //     size="small"
                                            //     color={
                                            //         column.required
                                            //             ? "primary"
                                            //             : "default"
                                            //     }
                                            //     variant={
                                            //         column.required
                                            //             ? "filled"
                                            //             : "outlined"
                                            //     }
                                            //     label={
                                            //         column.required
                                            //             ? `${column.columnName} *`
                                            //             : column.columnName
                                            //     }
                                            // />
                                            <FormControlLabel
                                                key={column.columnName}
                                                control={
                                                    <Checkbox
                                                        checked={
                                                            selectedColumns.includes(
                                                                column.columnName
                                                            )
                                                        }
                                                        onChange={(event) =>
                                                            handleColumnSelection(
                                                                column.columnName,
                                                                event.target.checked
                                                            )
                                                        }
                                                    />
                                                }
                                                label={column.columnName}
                                            />
                                        )
                                    )}

                                </Box>

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    mt={1}
                                >
                                    * Required field
                                </Typography>

                            </Paper>
                        )}


                    {/* ==========================================
                        CSV UPLOAD
                    ========================================== */}

                    {metadata && (

                        <Paper
                            variant="outlined"
                            sx={{
                                p: 3,
                                textAlign: "center",
                                borderStyle: "dashed",
                                borderWidth: 2
                            }}
                        >

                            {!file ? (

                                <>

                                    <CloudUpload
                                        sx={{
                                            fontSize: 52,
                                            color:
                                                "primary.main"
                                        }}
                                    />

                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                        mt={1}
                                    >
                                        Upload CSV File
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        mb={2}
                                    >
                                        CSV headers must match
                                        the selected table
                                    </Typography>

                                    <Button
                                        component="label"
                                        variant="contained"
                                        startIcon={
                                            <CloudUpload />
                                        }
                                        disabled={
                                            loadingImport
                                        }
                                    >
                                        Choose CSV File

                                        <input
                                            ref={
                                                fileInputRef
                                            }
                                            hidden
                                            type="file"
                                            accept=".csv,text/csv"
                                            onChange={
                                                handleFileChange
                                            }
                                        />

                                    </Button>

                                </>

                            ) : (

                                <Box>

                                    <InsertDriveFile
                                        sx={{
                                            fontSize: 45,
                                            color:
                                                "success.main"
                                        }}
                                    />

                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                    >
                                        {file.name}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {(
                                            file.size /
                                            1024
                                        ).toFixed(1)} KB
                                    </Typography>

                                    <Button
                                        size="small"
                                        color="error"
                                        startIcon={
                                            <Delete />
                                        }
                                        onClick={
                                            handleRemoveFile
                                        }
                                        disabled={
                                            loadingImport
                                        }
                                        sx={{
                                            mt: 1
                                        }}
                                    >
                                        Remove File
                                    </Button>

                                </Box>

                            )}

                        </Paper>
                    )}


                    {/* ==========================================
                        ERRORS
                    ========================================== */}

                    {errors.length > 0 && (

                        <Alert
                            severity="error"
                            icon={<ErrorIcon />}
                        >

                            <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                mb={0.5}
                            >
                                Validation Error
                            </Typography>

                            <Box
                                component="ul"
                                sx={{
                                    margin: 0,
                                    paddingLeft: 2
                                }}
                            >

                                {errors.map(
                                    (
                                        error,
                                        index
                                    ) => (

                                        <li key={index}>
                                            {error}
                                        </li>

                                    )
                                )}

                            </Box>

                        </Alert>
                    )}


                    {/* ==========================================
                        SUMMARY
                    ========================================== */}

                    {records.length > 0 && (

                        <Box>

                            <Stack
                                direction="row"
                                spacing={2}
                                flexWrap="wrap"
                                useFlexGap
                            >

                                {/* Total */}

                                <Paper
                                    variant="outlined"
                                    sx={{
                                        px: 3,
                                        py: 1.5,
                                        minWidth: 140
                                    }}
                                >

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Total Records
                                    </Typography>

                                    <Typography
                                        variant="h6"
                                        fontWeight={600}
                                    >
                                        {totalRecords}
                                    </Typography>

                                </Paper>


                                {/* Valid */}

                                <Paper
                                    variant="outlined"
                                    sx={{
                                        px: 3,
                                        py: 1.5,
                                        minWidth: 140
                                    }}
                                >

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Valid Records
                                    </Typography>

                                    <Typography
                                        variant="h6"
                                        color="success.main"
                                        fontWeight={600}
                                    >
                                        {validRecords.length}
                                    </Typography>

                                </Paper>


                                {/* Invalid */}

                                <Paper
                                    variant="outlined"
                                    sx={{
                                        px: 3,
                                        py: 1.5,
                                        minWidth: 140
                                    }}
                                >

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Invalid Records
                                    </Typography>

                                    <Typography
                                        variant="h6"
                                        color="error.main"
                                        fontWeight={600}
                                    >
                                        {invalidRecords.length}
                                    </Typography>

                                </Paper>

                            </Stack>

                        </Box>
                    )}


                    {/* ==========================================
                        PREVIEW
                    ========================================== */}

                    {records.length > 0 && (

                        <Box>

                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                mb={1}
                            >

                                <Box>

                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={600}
                                    >
                                        CSV Preview
                                    </Typography>

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Review the records
                                        before importing
                                    </Typography>

                                </Box>

                                {invalidRecords.length >
                                    0 && (

                                        <Chip
                                            size="small"
                                            color="error"
                                            icon={
                                                <ErrorIcon />
                                            }
                                            label={`${invalidRecords.length} errors`}
                                        />

                                    )}

                            </Box>


                            <TableContainer
                                component={Paper}
                                variant="outlined"
                                sx={{
                                    maxHeight: 450
                                }}
                            >

                                <Table
                                    stickyHeader
                                    size="small"
                                >

                                    <TableHead>

                                        <TableRow>

                                            <TableCell
                                                sx={{
                                                    fontWeight: 600
                                                }}
                                            >
                                                Row
                                            </TableCell>

                                            {importableColumns.map(
                                                column => (

                                                    <TableCell
                                                        key={
                                                            column.columnName
                                                        }
                                                        sx={{
                                                            fontWeight: 600,
                                                            whiteSpace:
                                                                "nowrap"
                                                        }}
                                                    >
                                                        {
                                                            column.columnName
                                                        }
                                                    </TableCell>

                                                )
                                            )}

                                            <TableCell
                                                sx={{
                                                    fontWeight: 600
                                                }}
                                            >
                                                Status
                                            </TableCell>

                                            <TableCell
                                                sx={{
                                                    fontWeight: 600,
                                                    minWidth: 250
                                                }}
                                            >
                                                Errors
                                            </TableCell>

                                        </TableRow>

                                    </TableHead>


                                    <TableBody>

                                        {records.map(
                                            (
                                                row,
                                                index
                                            ) => {

                                                const invalid =
                                                    row?._errors?.length > 0;

                                                return (

                                                    <TableRow
                                                        key={
                                                            index
                                                        }
                                                        hover
                                                        sx={{
                                                            backgroundColor:
                                                                invalid
                                                                    ? "error.50"
                                                                    : "success.50"
                                                        }}
                                                    >

                                                        <TableCell>
                                                            {
                                                                row._rowNumber
                                                            }
                                                        </TableCell>


                                                        {importableColumns.map(
                                                            column => (

                                                                <TableCell
                                                                    key={
                                                                        column.columnName
                                                                    }
                                                                    sx={{
                                                                        whiteSpace:
                                                                            "nowrap"
                                                                    }}
                                                                >
                                                                    {
                                                                        row[
                                                                        column.columnName
                                                                        ]
                                                                    }
                                                                </TableCell>

                                                            )
                                                        )}


                                                        <TableCell>

                                                            {invalid ? (

                                                                <Chip
                                                                    size="small"
                                                                    color="error"
                                                                    icon={
                                                                        <ErrorIcon />
                                                                    }
                                                                    label="Invalid"
                                                                />

                                                            ) : (

                                                                <Chip
                                                                    size="small"
                                                                    color="success"
                                                                    icon={
                                                                        <CheckCircle />
                                                                    }
                                                                    label="Valid"
                                                                />

                                                            )}

                                                        </TableCell>


                                                        <TableCell>

                                                            {invalid ? (

                                                                <Typography
                                                                    variant="caption"
                                                                    color="error.main"
                                                                >
                                                                    {
                                                                        row._errors.join(
                                                                            ", "
                                                                        )
                                                                    }
                                                                </Typography>

                                                            ) : (

                                                                <Typography
                                                                    variant="caption"
                                                                    color="success.main"
                                                                >
                                                                    No errors
                                                                </Typography>

                                                            )}

                                                        </TableCell>

                                                    </TableRow>
                                                );
                                            }
                                        )}

                                    </TableBody>

                                </Table>

                            </TableContainer>

                        </Box>
                    )}

                </Stack>

            </DialogContent>


            {/* ==================================================
                ACTIONS
            ================================================== */}

            <Divider />

            <DialogActions
                sx={{
                    px: 3,
                    py: 2
                }}
            >

                <Button
                    onClick={handleClose}
                    disabled={loadingImport}
                >
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    startIcon={
                        loadingImport
                            ? <CircularProgress
                                size={18}
                                color="inherit"
                            />
                            : <CheckCircle />
                    }
                    onClick={handleImport}
                    disabled={
                        loadingImport ||
                        !selectedTable ||
                        !file ||
                        records.length === 0 ||
                        validRecords.length === 0
                    }
                >
                    {loadingImport
                        ? "Importing..."
                        : `Import ${validRecords.length
                        } Records`
                    }
                </Button>

            </DialogActions>

        </Dialog>
    );
};

export default GenericCsvImportForm;

